'use client';

import { Suspense, lazy, useCallback, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { appendSearchParamsToUrl, removeSearchParamsFromUrl, sanitizeNavigationUrl, wait } from '@bridge/utils';
import { create } from 'zustand';

import ModalWrapper from '../components/ModalWrapper';

import { parseUrlOverlayProps } from './urlPropSchema';

import type { UrlPropSchema } from './urlPropSchema';
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react';
import type { UseBoundStore, StoreApi } from 'zustand';

/** Props injected by `ModalRenderer` into every modal component. */
export interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * True for the bottommost modal in the active stack, false for any modal opened on top of it.
   * `ModalWrapper` reads this to suppress its overlay backdrop on stacked modals so a single
   * darkening layer covers the page regardless of stack depth.
   */
  isFirstModal?: boolean;
}

// `ComponentType<any>` (rather than `ComponentType<BaseModalProps>`) lets the registry hold
// components whose prop shapes EXTEND BaseModalProps with required fields. Per-key prop types
// are recovered via `InferComponentProps<T>` and surfaced through `ModalProps<R, ID, Overrides>`,
// so call-site type safety is preserved. The renderer assumes every component accepts
// BaseModalProps (it injects `isOpen`/`onClose` unconditionally) — a documented contract,
// not a type-checked one.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ModalImportFnAny = () => Promise<{ default: ComponentType<any> }>;

/** Presentation returned by `renderErrorFallback`: the shell renders the title/description, the fallback the body. */
type ErrorFallbackContent = { title: string; description?: string; content: ReactNode };

/**
 * Object form of a registry entry that co-locates URL-openability with the component. The
 * `urlOpenable` schema is the opt-in trust boundary for `?modal=<id>&modalProps=<JSON>`: a modal
 * whose entry omits it — or that uses the bare-import form — can never be opened from the URL,
 * and props failing the schema are rejected. App-driven `openModal` is unaffected. See `UrlPropSchema`.
 */
export interface ModalRegistryEntryConfig {
  component: ModalImportFnAny;
  urlOpenable?: UrlPropSchema;
}

/**
 * A registry entry is either a bare dynamic import or the `{ component, urlOpenable }` config.
 * Use the config form only for modals opting in to URL opening; bare imports stay terse.
 */
export type ModalRegistryEntry = ModalImportFnAny | ModalRegistryEntryConfig;

export type ModalRegistryMap = Record<string, ModalRegistryEntry>;

type ModalImportFn = () => Promise<{ default: ComponentType<BaseModalProps> }>;

type InferComponentProps<T> = T extends ModalRegistryEntryConfig
  ? InferComponentProps<T['component']>
  : T extends () => Promise<{ default: ComponentType<infer P> }>
    ? P
    : never;

const resolveModalImport = (entry: ModalRegistryEntry): ModalImportFnAny =>
  typeof entry === 'function' ? entry : entry.component;

const resolveModalUrlSchema = (entry: ModalRegistryEntry): UrlPropSchema | undefined =>
  typeof entry === 'function' ? undefined : entry.urlOpenable;

// Distributes Omit across union variants so a modal whose props are a
// discriminated union keeps its variants instead of collapsing to the keys
// shared across all of them.
type DistributiveOmit<T, K extends keyof never> = T extends unknown ? Omit<T, K> : never;

type DefaultModalProps<R extends ModalRegistryMap, ID extends keyof R> = DistributiveOmit<
  InferComponentProps<R[ID]>,
  'isOpen' | 'onClose'
>;

/**
 * Resolved props for a modal identifier, accounting for optional per-modal overrides.
 * Apps pass an `Overrides` map to `createModalStore` when they want to accept a
 * narrower public surface than the component's full prop type.
 */
export type ModalProps<
  R extends ModalRegistryMap,
  ID extends keyof R,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> = ID extends keyof Overrides ? Overrides[ID] : DefaultModalProps<R, ID>;

export interface ModalInstance<R extends ModalRegistryMap, ID extends keyof R = keyof R> {
  id: string;
  identifier: ID;
  component: LazyExoticComponent<ComponentType<BaseModalProps>>;
  props: Record<string, unknown> | null;
  /**
   * Per-instance callback fired exactly once when this instance closes (via
   * `closeModal`, `closeAllModals`, or the renderer's auto-close path). Lets
   * the opener react to dismissal without threading state through props.
   */
  onClose?: () => void;
}

export interface OpenModalOptions {
  /** Fired when this specific instance closes. See `ModalInstance.onClose`. */
  onClose?: () => void;
}

interface ModalState<R extends ModalRegistryMap> {
  activeModals: ModalInstance<R>[];
}

interface ModalActions<R extends ModalRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> {
  openModal: <ID extends keyof R>(
    identifier: ID,
    props?: ModalProps<R, ID, Overrides>,
    options?: OpenModalOptions,
  ) => string;
  closeModal: (id: string) => Promise<void>;
  closeAllModals: () => void;
}

export type ModalStore<R extends ModalRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> = ModalState<R> &
  ModalActions<R, Overrides>;

export type GetModalUrl<R extends ModalRegistryMap, Overrides extends Partial<Record<keyof R, unknown>>> = <
  ID extends keyof R,
>(
  url: string,
  modalName: ID,
  modalProps?: ModalProps<R, ID, Overrides>,
) => string;

export interface UseModalReturn<
  R extends ModalRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> {
  activeModals: ModalInstance<R>[];
  openModal: ModalActions<R, Overrides>['openModal'];
  /**
   * Opens the modal and mirrors it in the URL via `?modal=<id>&modalProps=<encoded urlProps>`.
   * - `modalProps`: full payload for immediate display (can include rich objects).
   * - `urlProps`: minimal, serializable payload for deep linking (typically IDs).
   * Uses `history.replaceState` to avoid triggering an RSC refetch.
   */
  openModalWithDeepLink: <ID extends keyof R>(
    identifier: ID,
    modalProps: ModalProps<R, ID, Overrides>,
    urlProps: ModalProps<R, ID, Overrides>,
    options?: OpenModalOptions,
  ) => void;
  closeModal: (id: string) => Promise<void>;
  closeAllModals: () => void;
}

export interface CreateModalStoreOptions {
  /**
   * Base URL used by `getModalUrl` and `openModalWithDeepLink` when a relative URL is passed in.
   * Defaults to `window.location.origin` at call time, which only works client-side. Pass an
   * explicit value (e.g. an env-derived domain) when the deep-link helpers may run during SSR/RSC.
   */
  baseUrl?: string;
}

export interface CreateModalStoreResult<
  R extends ModalRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
> {
  useModalStore: UseBoundStore<StoreApi<ModalStore<R, Overrides>>>;
  useModal: () => UseModalReturn<R, Overrides>;
  preloadModals: () => Promise<void>;
  ModalRenderer: ComponentType<{
    isLoggedIn: boolean;
    /**
     * Contains a render error thrown by the active modal (e.g. a suspended query
     * rejecting) so it degrades to this fallback instead of crashing the app.
     * Omit to preserve today's behavior of letting the error escape uncaught.
     */
    renderErrorFallback?: (args: {
      error: unknown;
      reset: () => void;
    }) => ErrorFallbackContent;
  }>;
  /**
   * Builds a deep-link URL that opens the given modal with `modalProps` when followed.
   * The URL contract (`?modal=<id>&modalProps=<encoded JSON>`) round-trips with the
   * `ModalRenderer` returned from this same factory.
   */
  getModalUrl: GetModalUrl<R, Overrides>;
}

/**
 * Factory that returns a Zustand-backed modal store, a preloader, and a pre-wired
 * `<ModalRenderer />` closed over the store. Each app instantiates its own instance
 * with its registry, keeping @bridge/ui free of app-specific deps.
 *
 * Behavior contract:
 *   - **Singleton per identifier**: opening a modal whose identifier is already in the
 *     active stack returns the existing instance's id without updating props. Stacking
 *     *different* identifiers (e.g. a confirmation modal over a form modal) is supported.
 *   - **Per-instance `onClose`**: pass via `openModal(id, props, { onClose })`. Fires
 *     exactly once when the instance closes, via any close path (explicit `closeModal`,
 *     `closeAllModals`, or the renderer's auto-close).
 *   - **URL deep-linking**: `?modal=<id>&modalProps=<encoded JSON>` round-trips through
 *     the renderer. Apps that don't use deep-links simply never put `?modal=` in URLs.
 */
export function createModalStore<
  R extends ModalRegistryMap,
  Overrides extends Partial<Record<keyof R, unknown>> = object,
>(modalRegistry: R, factoryOptions: CreateModalStoreOptions = {}): CreateModalStoreResult<R, Overrides> {
  const { baseUrl: configuredBaseUrl } = factoryOptions;
  const resolveBaseUrl = (): string =>
    configuredBaseUrl ?? (typeof window !== 'undefined' ? window.location.origin : '');

  const getModalUrl: GetModalUrl<R, Overrides> = (url, modalName, modalProps) => {
    const params: Record<string, string> = { modal: String(modalName) };
    if (modalProps) {
      params.modalProps = encodeURIComponent(JSON.stringify(modalProps));
    }
    return appendSearchParamsToUrl(url, params, resolveBaseUrl());
  };

  const useModalStore = create<ModalStore<R, Overrides>>((set, get) => ({
    activeModals: [],

    openModal: (identifier, props, options) => {
      // Singleton per identifier — re-opening returns the existing instance.
      // Stacking *different* identifiers (e.g. ConfirmModal over a form modal) still works.
      const existing = get().activeModals.find((m) => m.identifier === identifier);
      if (existing) return existing.id;

      const entry = modalRegistry[identifier];

      if (!entry) {
        console.error(`Modal "${String(identifier)}" not found in registry`);
        return '';
      }

      const LazyComponent = lazy(resolveModalImport(entry) as ModalImportFn);
      const modalId = crypto.randomUUID();

      const newModal: ModalInstance<R> = {
        id: modalId,
        identifier,
        component: LazyComponent,
        props: (props as Record<string, unknown> | undefined) ?? null,
        onClose: options?.onClose,
      };

      set((state) => ({
        activeModals: [...state.activeModals, newModal],
      }));

      return modalId;
    },

    closeModal: async (id) => {
      const instance = get().activeModals.find((m) => m.id === id);
      // Capture + clear onClose before firing so a second closeModal(id) during
      // the close animation can't double-fire the callback.
      const externalOnClose = instance?.onClose;

      // First update: set isOpen to false for animation, strip onClose.
      set((state) => ({
        activeModals: state.activeModals.map((modal) =>
          modal.id === id
            ? {
                ...modal,
                onClose: undefined,
                props: { ...modal.props, isOpen: false },
              }
            : modal,
        ),
      }));

      externalOnClose?.();

      // Wait for animation to complete
      await wait(300);

      // Second update: remove the modal completely
      set((state) => ({
        activeModals: state.activeModals.filter((modal) => modal.id !== id),
      }));
    },

    closeAllModals: () => {
      get().activeModals.forEach((modal) => modal.onClose?.());
      set({ activeModals: [] });
    },
  }));

  const preloadModals = async (): Promise<void> => {
    const preloadPromises = Object.entries(modalRegistry).map(async ([identifier, entry]) => {
      try {
        await resolveModalImport(entry)();
      } catch (error) {
        console.error(`Failed to preload modal "${identifier}":`, error);
      }
    });

    await Promise.all(preloadPromises);
  };

  function useModal(): UseModalReturn<R, Overrides> {
    const activeModals = useModalStore((state) => state.activeModals);
    const openModal = useModalStore((state) => state.openModal);
    const closeModal = useModalStore((state) => state.closeModal);
    const closeAllModals = useModalStore((state) => state.closeAllModals);

    const openModalWithDeepLink = <ID extends keyof R>(
      identifier: ID,
      modalProps: ModalProps<R, ID, Overrides>,
      urlProps: ModalProps<R, ID, Overrides>,
      options?: OpenModalOptions,
    ): void => {
      openModal(identifier, modalProps, options);

      if (typeof window === 'undefined') return;

      const currentUrl = window.location.pathname + window.location.search;
      const modalUrl = getModalUrl(currentUrl, identifier, urlProps);
      window.history.replaceState(null, '', sanitizeNavigationUrl(modalUrl));
    };

    return {
      activeModals,
      openModal,
      openModalWithDeepLink,
      closeModal,
      closeAllModals,
    };
  }

  function ModalRenderer({
    isLoggedIn,
    renderErrorFallback,
  }: {
    isLoggedIn: boolean;
    renderErrorFallback?: (args: {
      error: unknown;
      reset: () => void;
    }) => ErrorFallbackContent;
  }): ReactNode {
    const activeModals = useModalStore((state) => state.activeModals);
    const openModal = useModalStore((state) => state.openModal);
    const closeModal = useModalStore((state) => state.closeModal);

    const searchParams = useSearchParams();
    const router = useRouter();

    // Sync modal state with URL search parameters (only when logged in)
    useEffect(() => {
      // Never open modals from URL params when not logged in.
      if (!isLoggedIn) {
        return;
      }

      const modalParam = searchParams?.get('modal') as keyof R | null;
      if (!modalParam || !(modalParam in modalRegistry)) return;

      // Opt-in gate: a modal whose registry entry has no `urlOpenable` schema is never opened from the URL.
      const urlPropSchema = resolveModalUrlSchema(modalRegistry[modalParam]);
      if (!urlPropSchema) return;

      if (activeModals.some((modal) => modal.identifier === modalParam)) return;

      const result = parseUrlOverlayProps(searchParams?.get('modalProps'), urlPropSchema);
      if (!result.ok) {
        console.warn(`Ignoring URL-opened modal "${String(modalParam)}": invalid or unvalidated props`);
        return;
      }

      // Validated URL input crossing into typed territory; the schema is the trust boundary for this cast.
      openModal(modalParam, result.data as ModalProps<R, typeof modalParam, Overrides>);
    }, [searchParams, openModal, isLoggedIn, activeModals]);

    // Override closeModal to also strip the deep-link params from the URL.
    const handleCloseModal = useCallback(
      (id: string) => {
        closeModal(id);

        // Only replace the URL when one of our params is actually present. Without this guard
        // every modal close fires an unnecessary RSC soft navigation.
        const params = new URLSearchParams(window.location.search);
        if (!params.has('modal') && !params.has('modalProps')) return;

        const currentUrl = window.location.href;
        const cleanUrl = removeSearchParamsFromUrl(currentUrl, ['modal', 'modalProps'], window.location.origin);
        router.replace(sanitizeNavigationUrl(cleanUrl));
      },
      [closeModal, router],
    );

    if (activeModals.length === 0) {
      return null;
    }

    return (
      <>
        {activeModals.map((modalInstance, index) => {
          const SpecificModal = modalInstance.component;
          const modalSpecificProps = modalInstance.props ?? {};

          const combinedProps: BaseModalProps & Record<string, unknown> = {
            isOpen: true,
            onClose: () => handleCloseModal(modalInstance.id),
            isFirstModal: index === 0,
            ...modalSpecificProps,
          };

          if (!renderErrorFallback) {
            return (
              <Suspense fallback={null} key={modalInstance.id}>
                <SpecificModal {...combinedProps} />
              </Suspense>
            );
          }

          const modalNode = (
            <Suspense fallback={null}>
              <SpecificModal {...combinedProps} />
            </Suspense>
          );

          return (
            <QueryErrorResetBoundary key={modalInstance.id}>
              {({ reset }) => (
                <ErrorBoundary
                  fallbackRender={({ error, resetErrorBoundary }) => {
                    const fallback = renderErrorFallback({ error, reset: resetErrorBoundary });
                    return (
                      <ModalWrapper
                        description={fallback.description}
                        isOpen={combinedProps.isOpen}
                        onClose={combinedProps.onClose}
                        title={fallback.title}
                      >
                        {fallback.content}
                      </ModalWrapper>
                    );
                  }}
                  onReset={reset}
                  resetKeys={[modalInstance.id]}
                >
                  {modalNode}
                </ErrorBoundary>
              )}
            </QueryErrorResetBoundary>
          );
        })}
      </>
    );
  }

  return { useModalStore, useModal, preloadModals, ModalRenderer, getModalUrl };
}
